/**
 * CustomTitleService
 * Manages custom chart titles in localStorage
 * Provides get/set/reset functionality for user-customized titles
 */

export interface CustomTitle {
  title: string;
  subtitle: string;
  timestamp: string;
  isCustom: true;
}

export interface TitleKey {
  chartType: 'funding' | 'ma' | 'country' | 'category' | 'acquisition-category' | 'comparison';
  timeframe?: string;
  comparisonType?: string; // for comparison charts: 'us-israel', 'us-europe', 'us-uk'
  filters?: Record<string, string[] | undefined>;
}

class CustomTitleServiceClass {
  private readonly STORAGE_KEY_PREFIX = 'custom-title-';
  private readonly VERSION = '1.0';

  /**
   * Normalize filters to ensure consistent key generation
   * Treats undefined, {}, and {key: []} all as "no filters"
   */
  private normalizeFilters(filters: Record<string, string[] | undefined> | undefined): Record<string, string[]> | undefined {
    if (!filters) return undefined;

    // Filter out empty or undefined values
    const normalized = Object.entries(filters)
      .filter(([_, value]) => value && value.length > 0)
      .reduce((acc, [key, value]) => ({...acc, [key]: value as string[]}), {} as Record<string, string[]>);

    // If no valid filters remain, return undefined
    return Object.keys(normalized).length > 0 ? normalized : undefined;
  }

  /**
   * Generate a unique key for storing custom titles
   */
  private generateKey(titleKey: TitleKey): string {
    const { chartType, timeframe, comparisonType, filters } = titleKey;

    let key = `${this.STORAGE_KEY_PREFIX}${chartType}`;

    if (timeframe) {
      key += `-${timeframe}`;
    }

    if (comparisonType) {
      key += `-${comparisonType}`;
    }

    // Normalize filters before hashing to ensure consistency
    const normalizedFilters = this.normalizeFilters(filters);
    if (normalizedFilters) {
      const filterHash = this.hashFilters(normalizedFilters);
      key += `-${filterHash}`;
    }

    return key;
  }

  /**
   * Create a simple hash from filters for storage key
   */
  private hashFilters(filters: Record<string, string[] | undefined>): string {
    const filterStr = JSON.stringify(
      Object.entries(filters)
        .filter(([_, value]) => value && value.length > 0)
        .sort(([a], [b]) => a.localeCompare(b))
    );

    // Simple hash function
    let hash = 0;
    for (let i = 0; i < filterStr.length; i++) {
      const char = filterStr.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Get custom title for a chart
   * Returns null if no custom title exists
   */
  getCustomTitle(titleKey: TitleKey): CustomTitle | null {
    try {
      const key = this.generateKey(titleKey);
      const stored = localStorage.getItem(key);

      if (!stored) {
        return null;
      }

      const parsed = JSON.parse(stored);

      // Validate structure
      if (parsed && parsed.title && parsed.isCustom) {
        return parsed as CustomTitle;
      }

      return null;
    } catch (error) {
      console.error('Error retrieving custom title:', error);
      return null;
    }
  }

  /**
   * Save custom title for a chart
   */
  setCustomTitle(titleKey: TitleKey, title: string, subtitle: string): void {
    try {
      const key = this.generateKey(titleKey);
      const customTitle: CustomTitle = {
        title,
        subtitle,
        timestamp: new Date().toISOString(),
        isCustom: true
      };

      const jsonString = JSON.stringify(customTitle);
      localStorage.setItem(key, jsonString);

      // Immediate verification - if this fails, localStorage isn't working
      const verification = localStorage.getItem(key);
      if (!verification || verification !== jsonString) {
        throw new Error(`localStorage verification failed. Key: ${key}`);
      }
    } catch (error) {
      console.error('Error saving custom title:', error);
      throw error;
    }
  }

  /**
   * Reset custom title for a chart (remove from storage)
   */
  resetCustomTitle(titleKey: TitleKey): void {
    try {
      const key = this.generateKey(titleKey);
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Error resetting custom title:', error);
    }
  }

  /**
   * Clear all custom titles
   */
  clearAllCustomTitles(): number {
    try {
      let count = 0;
      const keys: string[] = [];

      // Collect all keys with our prefix
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.STORAGE_KEY_PREFIX)) {
          keys.push(key);
        }
      }

      // Remove them
      keys.forEach(key => {
        localStorage.removeItem(key);
        count++;
      });

      return count;
    } catch (error) {
      console.error('Error clearing all custom titles:', error);
      return 0;
    }
  }

  /**
   * Get all custom titles (for debugging/export)
   */
  getAllCustomTitles(): Array<{ key: string; title: CustomTitle }> {
    try {
      const customTitles: Array<{ key: string; title: CustomTitle }> = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.STORAGE_KEY_PREFIX)) {
          const stored = localStorage.getItem(key);
          if (stored) {
            try {
              const parsed = JSON.parse(stored);
              if (parsed.isCustom) {
                customTitles.push({ key, title: parsed });
              }
            } catch (e) {
              // Skip invalid entries
            }
          }
        }
      }

      return customTitles;
    } catch (error) {
      console.error('Error getting all custom titles:', error);
      return [];
    }
  }

  /**
   * Check if a chart has a custom title
   */
  hasCustomTitle(titleKey: TitleKey): boolean {
    return this.getCustomTitle(titleKey) !== null;
  }
}

// Export singleton instance
export const customTitleService = new CustomTitleServiceClass();
